/**
 * dataManager.js — N.D.Tools Data Layer
 * Merges localStorage overrides with static defaults from data.js
 * Used by both public pages and the Admin panel.
 */

import { categories as defaultCategories, productsList as defaultProducts } from './data';

const KEYS = {
  PRODUCTS: 'ndtools_products',
  CATEGORIES: 'ndtools_categories',
  GALLERY: 'ndtools_gallery',
  SETTINGS: 'ndtools_settings',
};

// ─── PRODUCTS ────────────────────────────────────────────

export function getProducts() {
  try {
    const stored = localStorage.getItem(KEYS.PRODUCTS);
    if (stored) {
      const parsed = JSON.parse(stored);
      // 🔥 Auto-sync: merge default specs if missing from stored data
      return parsed.map(p => {
        const defaultProd = defaultProducts.find(dp => dp.id === p.id);
        if (defaultProd && defaultProd.specs && !p.specs) {
          return { ...p, specs: defaultProd.specs };
        }
        return p;
      });
    }
  } catch (e) { /* fall through */ }
  return defaultProducts.map(p => ({ ...p }));
}

export function saveProducts(list) {
  localStorage.setItem(KEYS.PRODUCTS, JSON.stringify(list));
}

export function addProduct(product) {
  const list = getProducts();
  list.push(product);
  saveProducts(list);
  return list;
}

export function updateProduct(id, updates) {
  const list = getProducts().map(p => p.id === id ? { ...p, ...updates } : p);
  saveProducts(list);
  return list;
}

export function deleteProduct(id) {
  const list = getProducts().filter(p => p.id !== id);
  saveProducts(list);
  return list;
}

// ─── CATEGORIES ──────────────────────────────────────────

export function getCategories() {
  try {
    const stored = localStorage.getItem(KEYS.CATEGORIES);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* fall through */ }
  return defaultCategories.map(c => ({ ...c }));
}

export function saveCategories(list) {
  localStorage.setItem(KEYS.CATEGORIES, JSON.stringify(list));
}

export function addCategory(category) {
  const list = getCategories();
  list.push(category);
  saveCategories(list);
  return list;
}

export function updateCategory(id, updates) {
  const list = getCategories().map(c => c.id === id ? { ...c, ...updates } : c);
  saveCategories(list);
  return list;
}

export function deleteCategory(id) {
  const list = getCategories().filter(c => c.id !== id);
  saveCategories(list);
  return list;
}

// ─── GALLERY ─────────────────────────────────────────────

const defaultGalleryImages = [
  'https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc',
  'https://images.unsplash.com/photo-1617038220319-276d3cfab638',
  'https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0',
];

export function getGalleryImages() {
  try {
    const stored = localStorage.getItem(KEYS.GALLERY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* fall through */ }
  return [...defaultGalleryImages];
}

export function saveGalleryImages(list) {
  localStorage.setItem(KEYS.GALLERY, JSON.stringify(list));
}

// ─── VIDEOS ──────────────────────────────────────────────

const defaultGalleryVideos = [
  {
    title: "Precision Rolling Mill in Action",
    youtubeId: "dQw4w9WgXcQ", // Placeholder
    thumbnail: "https://images.unsplash.com/photo-1504917595217-d4dc5f64d0b0"
  },
  {
    title: "Heavy Duty Hydraulic Press Demo",
    youtubeId: "dQw4w9WgXcQ", // Placeholder
    thumbnail: "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc"
  },
  {
    title: "Industrial Tools Manufacturing Process",
    youtubeId: "dQw4w9WgXcQ", // Placeholder
    thumbnail: "https://images.unsplash.com/photo-1617038220319-276d3cfab638"
  }
];

export function getGalleryVideos() {
  return [...defaultGalleryVideos];
}

// ─── SITE SETTINGS ───────────────────────────────────────

const defaultSettings = {
  companyName: 'N.D.Tools',
  tagline: 'Precision Tools for Professional Needs',
  phone1: '+91 97239 62464',
  phone2: '+91 96018 42434',
  email: 'nd.tools@yahoo.in',
  address: 'Sahjanand Ind Area, Street No.-1, Near Murlidhar Weigh Bridge, Kothariya Ring Road, Rajkot - 360002',
  whatsappNumber: '919723962464',
};  

export function getSiteSettings() {
  try {
    const stored = localStorage.getItem(KEYS.SETTINGS);
    if (stored) return { ...defaultSettings, ...JSON.parse(stored) };
  } catch (e) { /* fall through */ }
  return { ...defaultSettings };
}

export function saveSiteSettings(obj) {
  localStorage.setItem(KEYS.SETTINGS, JSON.stringify(obj));
}

// ─── RESET ───────────────────────────────────────────────

export function resetAllData() {
  Object.values(KEYS).forEach(k => localStorage.removeItem(k));
}
